import { IAddToWishlistSetting } from "@ecp-boxes/settingsPatterns/contentPatterns.scheme";
import {
  scrollIntoRowPosition,
  scrollIntoStackPosition,
} from "../../boxes/BoxListOfButtons/BoxListOfButtons.methods";
import {
  ILinkContent,
  PopupActionsList,
  SelectedGoToOption,
  TRowId,
} from "../../settingsPatterns/contentPatterns.types";
import { PortalPath } from "../../shared/portalPath/portalPath";
import { useDynamicPageContext } from "../../structure/Contexts/DynamicPageContext";
import { useRowPopupContext } from "../../structure/Contexts/RowPopupContext";
import { useSectionEffectsContext } from "../../structure/Contexts/SectionEffectsContext";
import useAddToWishlistLogic from "./useAddToWishlistLogic";
import { useCallback, useEffect, useRef } from "react";
import useEndpointAction from "../../boxes/BoxButton/useEndpointAction";
import { throttle } from "lodash";

export const getButtonPaths = (button: ILinkContent) =>
  Object.values(button.forceOrderInsideStack ?? {}).flatMap((el) => el);

export const useLinkContentActions = <T extends HTMLElement>(
  path?: PortalPath,
  id?: number,
  addToWishlistSetting?: IAddToWishlistSetting,
  content?: ILinkContent
) => {
  const { effectsData } = useSectionEffectsContext();
  const {
    activePopup,
    setActivePopup,
    closeActivePopup,
    popupActions,
    setPopupPlacement,
  } = useRowPopupContext();
  const { setActivePaths, activePaths } = useDynamicPageContext();

  const { addToWishlistButton, addRemoveWishlistFunction, isOnWishlist } =
    useAddToWishlistLogic(undefined, addToWishlistSetting);

  const popupRef = useRef<T>(null);
  const resizeObserver = useRef<ResizeObserver | null>(null);

  const setPlacementForPopup = useCallback(() => {
    popupRef?.current &&
      setPopupPlacement({
        top: popupRef.current?.getBoundingClientRect().bottom,
        left: popupRef.current?.getBoundingClientRect().left,
        width: popupRef.current.getBoundingClientRect().width,
      });
  }, [setPopupPlacement]);

  const throttledSetPlacementForPopup = useCallback(
    throttle(setPlacementForPopup, 300),
    [setPlacementForPopup]
  );

  const { handleSnackbar } = useEndpointAction(content);

  const setNewResizeObserver = () => {
    resizeObserver.current = new ResizeObserver(throttledSetPlacementForPopup);
    resizeObserver.current.observe(window.document.body);
  };

  const getHref = (el: ILinkContent) => {
    if (
      el.selectedGoToOption === SelectedGoToOption.GO_TO_PAGE &&
      el.goToPageSetting?.link
    ) {
      return el.goToPageSetting.link;
    }
  };

  const getTarget = (el: ILinkContent) => {
    if (
      el.selectedGoToOption === SelectedGoToOption.GO_TO_PAGE &&
      el.goToPageSetting?.openInNewTab
    ) {
      return "_blank";
    }
  };

  const goToRow = (link: ILinkContent) => {
    if (path === undefined) return;
    closeActivePopup();
    scrollIntoRowPosition(link, path, effectsData);
  };

  const openPopup = (link: ILinkContent | TRowId) => {
    const rowIdNumber = Number(
      typeof link === "object" ? link?.openPopupSetting?.rowId : link
    );
    if (!Number.isNaN(rowIdNumber)) {
      setActivePopup(new PortalPath(`main:x,x-section:x,${rowIdNumber}`));
    }

    setPlacementForPopup();
    setNewResizeObserver();
  };

  const closePopup = (link: ILinkContent | TRowId) => {
    const rowIdNumber = Number(
      typeof link === "object" ? link?.closePopupSetting?.rowId : link
    );

    if (!Number.isNaN(rowIdNumber)) {
      closeActivePopup(new PortalPath(`main:x,x-section:x,${rowIdNumber}`));
    }
  };

  useEffect(() => {
    if (activePopup) return;

    resizeObserver.current?.disconnect();
  }, [activePopup]);

  const changeOrderInsideStack = (link: ILinkContent) => {
    if (id === undefined || path === undefined) return;
    scrollIntoStackPosition(link, effectsData);
    setActivePaths(
      id,
      getButtonPaths(link).map((el) => new PortalPath(el))
    );
  };

  const initFirstActivePaths = (link: ILinkContent) => {
    if (id === undefined) return;
    if (
      link.selectedGoToOption ===
        SelectedGoToOption.CHANGE_ORDER_INSIDE_STACK &&
      link.forceOrderInsideStack
    ) {
      setActivePaths(
        id,
        getButtonPaths(link).map((el) => new PortalPath(el))
      );
    }
  };

  const getIsActiveButton = (link: ILinkContent) => {
    if (id === undefined)
      return { isActiveButton: false, isBoxSelected: false };

    const buttonPaths = getButtonPaths(link);
    const flatActivePaths = activePaths.flatMap(
      (activePath) => activePath.paths
    );

    return {
      isActiveButton: flatActivePaths.some((path) =>
        buttonPaths.some((buttonPath) => path.isPathEqual(buttonPath))
      ),
      isBoxSelected: !buttonPaths.every(
        (path) => new PortalPath(path).getLastPublication() === "x"
      ),
    };
  };

  const anyActionIsActive = ({
    goToPageSetting,
    goToRowSetting,
    openPopupSetting,
    closePopupSetting,
  }: ILinkContent): boolean => {
    const linkExists =
      goToPageSetting &&
      goToPageSetting.link != null &&
      goToPageSetting.link.length > 0;

    const rowIdExists =
      goToRowSetting &&
      goToRowSetting.rowId != null &&
      goToRowSetting.rowId.toString().length > 0;

    const openRowIdExists =
      openPopupSetting &&
      openPopupSetting.rowId != null &&
      openPopupSetting.rowId.toString().length > 0;

    const closeRowIdExists =
      closePopupSetting &&
      closePopupSetting.rowId != null &&
      closePopupSetting.rowId.toString().length > 0;

    return (
      (linkExists || rowIdExists || openRowIdExists || closeRowIdExists) ??
      false
    );
  };
  const popupActionsHandler = (el: ILinkContent) => {
    if (el.actionPopupSetting === PopupActionsList.CLOSE_POPUP) {
      closeActivePopup();
    }
    if (el.actionPopupSetting === PopupActionsList.CONFIRM) {
      popupActions?.actionFunction();
    }
  };

  const linkContentActions = (link: ILinkContent) => {
    switch (link.selectedGoToOption) {
      case SelectedGoToOption.GO_TO_PAGE:
        return closeActivePopup();
      case SelectedGoToOption.GO_TO_ROW:
        return goToRow(link);
      case SelectedGoToOption.OPEN_POPUP:
        return openPopup(link);
      case SelectedGoToOption.CLOSE_POPUP:
        return closePopup(link);
      case SelectedGoToOption.CHANGE_ORDER_INSIDE_STACK:
        return changeOrderInsideStack(link);
      case SelectedGoToOption.ADD_TO_WISHLIST:
        return addRemoveWishlistFunction();
      case SelectedGoToOption.POPUP_ACTION:
        return popupActionsHandler(link);
      case SelectedGoToOption.ENDPOINT_ACTION:
        return handleSnackbar();
    }
  };

  return {
    linkContentActions,
    getHref,
    activePaths,
    getTarget,
    initFirstActivePaths,
    getIsActiveButton,
    anyActionIsActive,
    addToWishlistButton,
    isOnWishlist,
    popupRef,
    openPopup,
    closePopup,
  };
};
